import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useGridCopyMenu } from './gridCommon';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface BizDayRow {
  d: string;
  ty: number;
  bizYn: boolean;
  trdYn: boolean;
  openYn: boolean;
  sttlYn: boolean;
}

interface BizDaySummary {
  today: string;
  maxD: string | null;
  lastBizday: string | null;
  stale: boolean;
  missingDays: number;
  totalRows: number;
}

interface BizDayResponse {
  summary: BizDaySummary;
  rows: BizDayRow[];
}

const fmtDate = (s: string | null | undefined) => (s ? s.slice(0, 10) : '');

const WDAY = ['일', '월', '화', '수', '목', '금', '토'];

/** axios 에러에서 화면에 표시할 실패 원인 추출 */
const describeAxiosError = (e: any): string => {
  const d = e?.response?.data;
  const serverMsg =
    typeof d === 'string' ? d
    : d?.error ?? d?.detail ?? d?.title ?? d?.message ?? null;
  const status = e?.response?.status;
  if (status) {
    const label = status === 401 ? '인증 만료(로그인 필요)'
      : status === 403 ? '권한 없음'
      : status === 404 ? '엔드포인트 없음'
      : status >= 500 ? '서버 오류'
      : '요청 오류';
    return serverMsg ? `HTTP ${status} ${label}: ${serverMsg}` : `HTTP ${status} ${label}`;
  }
  if (e?.code === 'ECONNABORTED') return '요청 시간 초과 (서버 응답 지연)';
  if (e?.code === 'ERR_NETWORK') return '서버에 연결할 수 없음 (API 서버 중지 또는 네트워크 오류)';
  return e?.message || '알 수 없는 오류';
};

const boolCellRenderer = (p: any) => {
  const v = !!p.value;
  return (
    <span style={{
      color: v ? '#065f46' : '#9ca3af',
      fontWeight: v ? 700 : 400,
    }}>
      {v ? 'Y' : 'N'}
    </span>
  );
};

const BizDayManagement: React.FC = () => {
  const axios = getMainAxios();
  const copyMenu = useGridCopyMenu();

  const today = new Date();
  const iso = (dt: Date) => dt.toISOString().slice(0, 10);

  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth() - 2, 1)));
  const [to, setTo] = useState(iso(new Date(today.getFullYear(), today.getMonth() + 3, 0)));
  const [data, setData] = useState<BizDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<BizDayResponse>('/api/v1/admin/bizday', {
        params: { from, to },
      });
      setData(res.data);
    } catch (e) {
      console.error('영업일 조회 실패:', e);
      setMessage(`✗ 영업일 조회 실패 — ${describeAxiosError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [axios, from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // KIS CTCA0903R 수집 실행
  const handleCollect = useCallback(async () => {
    const fromYmd = from.replace(/-/g, '');
    if (!confirm(`KIS 휴장일조회(CTCA0903R)로 ${from} 이후 영업일 달력을 수집합니다.\n계속하시겠습니까?`)) return;
    setCollecting(true);
    setMessage('');
    try {
      const res = await axios.post<{ success: boolean; message: string; output?: string }>(
        '/api/v1/admin/bizday/collect',
        null,
        { params: { from: fromYmd, pages: 20 }, timeout: 130_000 },
      );
      setMessage(`✓ ${res.data.message}${res.data.output ? `\n${res.data.output}` : ''}`);
      await fetchData();
    } catch (e) {
      console.error('영업일 수집 실패:', e);
      setMessage(`✗ 영업일 수집 실패 — ${describeAxiosError(e)}`);
    } finally {
      setCollecting(false);
    }
  }, [axios, from, fetchData]);

  // 셀 편집 → 저장
  const handleCellChanged = useCallback(async (e: any) => {
    const r: BizDayRow = e.data;
    try {
      await axios.post('/api/v1/admin/bizday', {
        d: fmtDate(r.d),
        ty: r.ty,
        bizYn: r.bizYn,
        trdYn: r.trdYn,
        openYn: r.openYn,
        sttlYn: r.sttlYn,
      });
      setMessage(`✓ ${fmtDate(r.d)} 저장됨`);
      await fetchData();
    } catch (err) {
      setMessage(`✗ 저장 실패 — ${describeAxiosError(err)}`);
    }
  }, [axios, fetchData]);

  const colDefs = useMemo<ColDef<BizDayRow>[]>(() => [
    {
      headerName: '일자', field: 'd', width: 130,
      valueFormatter: (p) => fmtDate(p.value),
      cellStyle: { fontFamily: 'monospace', fontWeight: 600 },
    },
    {
      headerName: '요일', field: 'ty', width: 80,
      valueFormatter: (p) => WDAY[p.value] ?? String(p.value ?? ''),
      cellStyle: (p: any) => ({
        textAlign: 'center',
        color: p.value === 0 ? '#dc2626' : p.value === 6 ? '#2563eb' : '#374151',
      }),
    },
    { headerName: '영업일', field: 'bizYn', width: 100, editable: true,
      cellRenderer: boolCellRenderer, cellEditor: 'agCheckboxCellEditor',
      cellStyle: { textAlign: 'center' } },
    { headerName: '거래일', field: 'trdYn', width: 100, editable: true,
      cellRenderer: boolCellRenderer, cellEditor: 'agCheckboxCellEditor',
      cellStyle: { textAlign: 'center' } },
    { headerName: '개장일', field: 'openYn', width: 100, editable: true,
      cellRenderer: boolCellRenderer, cellEditor: 'agCheckboxCellEditor',
      cellStyle: { textAlign: 'center' } },
    { headerName: '결제일', field: 'sttlYn', width: 100, editable: true,
      cellRenderer: boolCellRenderer, cellEditor: 'agCheckboxCellEditor',
      cellStyle: { textAlign: 'center' } },
  ], []);

  const summary = data?.summary;

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>

      {/* 상단 헤더 카드 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
            영업일 관리
          </h3>

          <label style={{ fontSize: '12px', color: '#374151' }}>
            시작{' '}
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              style={{ fontSize: '12px', padding: '2px 6px', border: '1px solid #93c5fd', borderRadius: 4 }} />
          </label>
          <label style={{ fontSize: '12px', color: '#374151' }}>
            종료{' '}
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              style={{ fontSize: '12px', padding: '2px 6px', border: '1px solid #93c5fd', borderRadius: 4 }} />
          </label>

          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              padding: '4px 12px', background: '#3b82f6', color: 'white', border: 'none',
              borderRadius: '4px', cursor: loading ? 'wait' : 'pointer', fontSize: '12px', fontWeight: 500,
            }}
          >
            {loading ? '⏳ 조회 중...' : '🔄 조회'}
          </button>

          <button
            onClick={handleCollect}
            disabled={collecting}
            style={{
              padding: '4px 12px',
              background: collecting ? '#9ca3af' : '#059669',
              color: 'white', border: 'none', borderRadius: '4px',
              cursor: collecting ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 500,
            }}
          >
            {collecting ? '⏳ 수집중...' : '⬇ KIS 달력 수집'}
          </button>
        </div>

        {/* 달력 상태 요약 */}
        {summary && (
          <div style={{
            marginTop: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap',
            fontSize: '12px', color: '#374151',
          }}>
            <span>오늘: <b>{fmtDate(summary.today)}</b></span>
            <span>
              최근 영업일:{' '}
              <b style={{ color: summary.stale ? '#b91c1c' : '#065f46' }}>
                {fmtDate(summary.lastBizday) || '없음'}
              </b>
            </span>
            <span>
              달력 마지막:{' '}
              <b style={{ color: summary.stale ? '#b91c1c' : '#065f46' }}>
                {fmtDate(summary.maxD) || '없음'}
              </b>
            </span>
            <span>등록 건수: <b>{summary.totalRows.toLocaleString()}</b></span>
          </div>
        )}

        {summary?.stale && (
          <div style={{
            marginTop: '10px', padding: '8px 12px', borderRadius: '4px', fontSize: '12px',
            background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
          }}>
            ⚠ 영업일 달력이 오늘({fmtDate(summary.today)})까지 채워져 있지 않습니다
            {summary.maxD && ` — 마지막 등록일 ${fmtDate(summary.maxD)}, ${summary.missingDays}일 부족`}.
            수집현황 화면의 지연 판정이 '판정불가'로 표시됩니다. [KIS 달력 수집]을 실행하세요.
          </div>
        )}

        {message && (
          <div style={{
            marginTop: '12px', padding: '8px 12px', borderRadius: '4px', fontSize: '12px',
            background: message.startsWith('✓') ? '#d1fae5' : '#fee2e2',
            color:      message.startsWith('✓') ? '#065f46' : '#991b1b',
            border: message.startsWith('✓') ? '1px solid #6ee7b7' : '1px solid #fca5a5',
            whiteSpace: 'pre-wrap',
          }}>
            {message}
          </div>
        )}
      </div>

      {/* 영업일 그리드 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '16px',
      }}>
        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
          영업일 목록 ({data?.rows.length ?? 0}건)
          <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280' }}>
            — 영업일/거래일/개장일/결제일 셀을 더블클릭하면 수정됩니다
          </span>
        </div>
        <div className="ag-theme-quartz" style={{ height: 600, width: '100%', fontSize: '12px' }}>
          <AgGridReact<BizDayRow>
            rowData={data?.rows ?? []}
            columnDefs={colDefs}
            defaultColDef={{ sortable: true, filter: true, resizable: true }}
            onCellValueChanged={handleCellChanged}
            getRowStyle={(p: any) =>
              fmtDate(p.data?.d) === fmtDate(summary?.today)
                ? { backgroundColor: '#dbeafe', fontWeight: 600 }
                : undefined
            }
            rowHeight={28}
            headerHeight={32}
            {...copyMenu.gridProps}
          />
        </div>
      </div>

      {copyMenu.menu}
    </div>
  );
};

export default BizDayManagement;
